import { apiClient } from './client';
import type { ApiResponse } from '@/types';

export interface GymLocation {
  latitude: number;
  longitude: number;
}

export type GymStatus = 'ACTIVE' | 'PAUSED';
export type GymApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface Gym {
  gymId: string;
  name: string;
  description: string;
  profilePhotoUrl?: string;
  ownerId: string;
  phoneNumber: string;
  email: string;
  address: string; // Street address as a string
  city: string;
  state: string;
  pincode: string;
  location: GymLocation;
  geohash?: string;
  status: GymStatus;
  approvalStatus: GymApprovalStatus;
  rejectionReason?: string;
  openTime: string;
  closeTime: string;
  totalCapacity: number;
  totalMembers?: number;
  totalBookings?: number;
  totalReviews?: number;
  averageRating?: number;
  createdAt: string;
  updatedAt: string;
}

export interface GetGymsResponse {
  gyms: Gym[];
}

export interface DeleteGymResponse {
  message: string;
}

export interface CreateGymRequest {
  name: string;
  description: string;
  phoneNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  location: {
    latitude: number;
    longitude: number;
  };
  openTime: string;
  closeTime: string;
  totalCapacity: number;
}

export interface CreateGymResponse {
  gym: Gym;
}

/**
 * Get all gyms owned by the current user
 */
export const getGymsApi = async (): Promise<GetGymsResponse> => {
  const response = await apiClient.get<ApiResponse<GetGymsResponse>>('/v1/gym-owner/gyms');
  return response.data.data;
};

/**
 * Delete a gym by ID
 */
export const deleteGymApi = async (gymId: string): Promise<DeleteGymResponse> => {
  const response = await apiClient.delete<ApiResponse<DeleteGymResponse>>(
    `/v1/gym-owner/gyms/${gymId}`
  );
  return response.data.data;
};

/**
 * Create a new gym
 */
export const createGymApi = async (data: CreateGymRequest): Promise<CreateGymResponse> => {
  const response = await apiClient.post<ApiResponse<CreateGymResponse>>('/v1/gym-owner/gyms', data);
  return response.data.data;
};

export interface UpdateGymRequest {
  name?: string;
  address?: string;
  description?: string;
  phoneNumber?: string;
  email?: string;
  openTime?: string;
  closeTime?: string;
  totalCapacity?: number;
}

export interface UpdateGymResponse {
  gym: Gym;
}

/**
 * Update a gym by ID (partial update)
 */
export const updateGymApi = async (
  gymId: string,
  data: UpdateGymRequest
): Promise<UpdateGymResponse> => {
  const response = await apiClient.patch<ApiResponse<UpdateGymResponse>>(
    `/v1/gym-owner/gyms/${gymId}`,
    data
  );
  return response.data.data;
};

export interface PauseGymResponse {
  gym: Gym;
}

export interface ActivateGymResponse {
  gym: Gym;
}

/**
 * Pause a gym by ID
 */
export const pauseGymApi = async (gymId: string): Promise<PauseGymResponse> => {
  const response = await apiClient.patch<ApiResponse<PauseGymResponse>>(
    `/v1/gym-owner/gyms/${gymId}/pause`
  );
  return response.data.data;
};

/**
 * Activate a gym by ID
 */
export const activateGymApi = async (gymId: string): Promise<ActivateGymResponse> => {
  const response = await apiClient.patch<ApiResponse<ActivateGymResponse>>(
    `/v1/gym-owner/gyms/${gymId}/activate`
  );
  return response.data.data;
};

// ============================================================================
// Gym Files/Photos API
// ============================================================================

export type GymPhotoType = 'LOGO' | 'CAROUSEL';

export interface GymPhoto {
  photoId: string;
  url: string;
  photoType: GymPhotoType;
  displayOrder: number;
  uploadedAt: string;
}

export interface UploadGymFileRequest {
  gymId: string;
  file: File;
  photoType: GymPhotoType;
  displayOrder: number;
}

export interface UploadGymFileResponse {
  photo: GymPhoto;
}

export interface GetGymFilesResponse {
  photos: GymPhoto[];
}

export interface DeleteGymFileResponse {
  message: string;
}

/**
 * Upload a file (logo or carousel image) for a gym
 */
export const uploadGymFileApi = async ({
  gymId,
  file,
  photoType,
  displayOrder,
}: UploadGymFileRequest): Promise<UploadGymFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('photoType', photoType);
  formData.append('displayOrder', displayOrder.toString());

  // Don't set Content-Type header manually - axios will set it automatically with the correct boundary
  const response = await apiClient.post<ApiResponse<UploadGymFileResponse>>(
    `/v1/gym-owner/gyms/${gymId}/uploads/file`,
    formData
  );
  return response.data.data;
};

/**
 * Get all uploaded files/photos for a gym
 */
export const getGymFilesApi = async (gymId: string): Promise<GetGymFilesResponse> => {
  const response = await apiClient.get<ApiResponse<GetGymFilesResponse>>(
    `/v1/gym-owner/gyms/${gymId}/uploads`
  );
  return response.data.data;
};

/**
 * Delete a gym photo by ID
 */
export const deleteGymFileApi = async (
  gymId: string,
  photoId: string
): Promise<DeleteGymFileResponse> => {
  const response = await apiClient.delete<ApiResponse<DeleteGymFileResponse>>(
    `/v1/gym-owner/gyms/${gymId}/uploads/${photoId}`
  );
  return response.data.data;
};

// ============================================================================
// Profile Photo API
// ============================================================================

export interface UploadProfilePhotoResponse {
  profilePhotoUrl: string;
  gym: Gym;
}

/**
 * Upload the gym's profile/logo photo
 */
export const uploadProfilePhotoApi = async (
  gymId: string,
  file: File
): Promise<UploadProfilePhotoResponse> => {
  const formData = new FormData();
  formData.append('photo', file);
  const response = await apiClient.post<ApiResponse<UploadProfilePhotoResponse>>(
    `/v1/gym-owner/gyms/${gymId}/profile-photo`,
    formData,
    { headers: { 'Content-Type': undefined } }
  );
  return response.data.data;
};

// ============================================================================
// Gallery Photos API
// ============================================================================

export interface GymGalleryPhoto {
  photoId: string;
  url: string;
  caption: string;
  displayOrder: number;
  uploadedAt: string;
}

export interface GetGalleryPhotosResponse {
  photos: GymGalleryPhoto[];
}

export interface UploadGalleryPhotoResponse {
  photo: GymGalleryPhoto;
}

export interface UpdateGalleryPhotoCaptionResponse {
  photo: GymGalleryPhoto;
}

export interface ReorderGalleryPhotosResponse {
  photos: GymGalleryPhoto[];
}

export interface DeleteGalleryPhotoResponse {
  message: string;
}

/**
 * Get all gallery photos for a gym
 */
export const getGalleryPhotosApi = async (gymId: string): Promise<GetGalleryPhotosResponse> => {
  const response = await apiClient.get<ApiResponse<GetGalleryPhotosResponse>>(
    `/v1/gym-owner/gyms/${gymId}/photos`
  );
  return response.data.data;
};

/**
 * Upload a gallery photo
 */
export const uploadGalleryPhotoApi = async (
  gymId: string,
  file: File,
  caption?: string
): Promise<UploadGalleryPhotoResponse> => {
  const formData = new FormData();
  formData.append('photo', file);
  if (caption) formData.append('caption', caption);
  const response = await apiClient.post<ApiResponse<UploadGalleryPhotoResponse>>(
    `/v1/gym-owner/gyms/${gymId}/photos`,
    formData,
    { headers: { 'Content-Type': undefined } }
  );
  return response.data.data;
};

/**
 * Reorder gallery photos — send full ordered array of photoIds
 */
export const reorderGalleryPhotosApi = async (
  gymId: string,
  orderedPhotoIds: string[]
): Promise<ReorderGalleryPhotosResponse> => {
  const response = await apiClient.patch<ApiResponse<ReorderGalleryPhotosResponse>>(
    `/v1/gym-owner/gyms/${gymId}/photos/reorder`,
    { orderedPhotoIds }
  );
  return response.data.data;
};

/**
 * Update a gallery photo's caption
 */
export const updateGalleryPhotoCaptionApi = async (
  gymId: string,
  photoId: string,
  caption: string
): Promise<UpdateGalleryPhotoCaptionResponse> => {
  const response = await apiClient.patch<ApiResponse<UpdateGalleryPhotoCaptionResponse>>(
    `/v1/gym-owner/gyms/${gymId}/photos/${photoId}`,
    { caption }
  );
  return response.data.data;
};

/**
 * Delete a gallery photo
 */
export const deleteGalleryPhotoApi = async (
  gymId: string,
  photoId: string
): Promise<DeleteGalleryPhotoResponse> => {
  const response = await apiClient.delete<ApiResponse<DeleteGalleryPhotoResponse>>(
    `/v1/gym-owner/gyms/${gymId}/photos/${photoId}`
  );
  return response.data.data;
};
